import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import Typo from 'constants/typography'
import { requestWithCache } from 'lib/api'
import { ENDPOINTS } from 'lib/api/const'
import CategoryProductList from 'pages/category/list'
import { buildSearchSuggestions, expandSearchQuery } from '../utils'
import styles from './style.scss'

const MIN_LENGTH = 2
const DEBOUNCE_MS = 250
// enough results to word the suggestions from, in one request
const SAMPLE_SIZE = 48
// how many products to preview in the dropdown while typing (ASOS style)
const PREVIEW_COUNT = 6

const useDebouncedValue = (value, delay) => {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debounced
}

// Bold the part the shopper typed, as ASOS does ("Dres" + "s").
const highlight = (suggestion, term) => {
  const start = suggestion.toLowerCase().indexOf(term.toLowerCase())
  if (!term || start < 0) return suggestion

  const end = start + term.length
  return (
    <React.Fragment>
      {suggestion.slice(0, start)}
      <strong>{suggestion.slice(start, end)}</strong>
      {suggestion.slice(end)}
    </React.Fragment>
  )
}

// TECH-20: suggestions while typing, ASOS style. Shows `children` (recent and
// popular searches) until there is something to suggest.
const SearchSuggestions = ({ children, isPhone, onSelect, term }) => {
  const typed = (term || '').trim()
  const query = useDebouncedValue(typed, DEBOUNCE_MS)
  const isActive = query.length >= MIN_LENGTH
  const lastResults = useRef({ query: '', suggestions: [], products: [] })

  const { data } = useSWR(
    isActive
      ? [
          ENDPOINTS.productsSearch.link,
          JSON.stringify({
            params: {
              // expand intent phrases (date night, summer vibes, ชุดทำงาน...)
              // so the type-ahead preview matches the results page
              query: expandSearchQuery(query),
              'filter[skip]': 0,
              'filter[limit]': SAMPLE_SIZE,
              'filter[order_by]': 'popular',
            },
          }),
        ]
      : null,
    requestWithCache,
    { dedupingInterval: ENDPOINTS.productsSearch.cache },
  )

  if (data?.products) {
    lastResults.current = {
      query,
      suggestions: buildSearchSuggestions(data.products, query),
      products: data.products,
    }
  }

  // keep the previous results while the next load runs, so nothing flickers
  const active = isActive && typed.length >= MIN_LENGTH
  const suggestions = active ? lastResults.current.suggestions : []
  // ASOS style: preview a few real products while the shopper types
  const products = active
    ? lastResults.current.products.slice(0, PREVIEW_COUNT)
    : []

  if (!suggestions.length && !products.length) {
    return children
  }

  return (
    <div className="search-suggestions" data-cy="search-suggestions">
      <style jsx>{styles}</style>
      {suggestions.length > 0 && (
        <ul className="search-suggestions__list">
          {suggestions.map((suggestion) => (
            <li key={suggestion}>
              <button
                type="button"
                className={clsx(Typo.body2, 'search-suggestions__item')}
                // read as one phrase, not split by the bold part
                aria-label={suggestion}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(suggestion)}
              >
                {highlight(suggestion, typed)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {products.length > 0 && (
        <div
          className="search-suggestions__products"
          data-cy="search-suggestions-products"
        >
          <CategoryProductList
            displayHoverInteractions={false}
            isSearchPage
            isPhone={isPhone}
            list={products}
            refId={query}
            refType="search-suggestion"
          />
        </div>
      )}
    </div>
  )
}

SearchSuggestions.propTypes = {
  children: PropTypes.node,
  isPhone: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  term: PropTypes.string,
}

SearchSuggestions.defaultProps = {
  children: null,
  isPhone: false,
  term: '',
}

export default SearchSuggestions
